import { create } from 'zustand';
import { vaultApi } from '../api/vaultApi';
import type { VaultPack } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

interface AdminVaultState {
  packs: VaultPack[];
  load: LoadState;
  loadingIds: string[];

  loadPacks: () => Promise<void>;
  createPack: (pack: VaultPack) => Promise<VaultPack>;
  updatePack: (id: string, patch: Partial<VaultPack>) => Promise<VaultPack>;
  deletePack: (id: string) => Promise<void>;
  reset: () => void;
}

export const useAdminVaultStore = create<AdminVaultState>((set) => ({
  packs: [],
  load: IDLE_LOAD,
  loadingIds: [],

  async loadPacks() {
    set({ load: LOADING_LOAD });
    try {
      const packs = await vaultApi.getAdminPacks();
      set({ packs, load: READY_LOAD });
    } catch (err) {
      set({ load: failedLoad(err) });
    }
  },

  async createPack(pack) {
    const created = await vaultApi.createAdminPack(pack);
    set((s) => ({ packs: [created, ...s.packs] }));
    return created;
  },

  async updatePack(id, patch) {
    set((s) => ({ loadingIds: [...s.loadingIds, id] }));
    try {
      const updated = await vaultApi.updateAdminPack(id, patch);
      set((s) => ({
        packs: s.packs.map((candidate) => (candidate.id === id ? updated : candidate)),
        loadingIds: s.loadingIds.filter((candidate) => candidate !== id),
      }));
      return updated;
    } catch (err) {
      set((s) => ({ loadingIds: s.loadingIds.filter((candidate) => candidate !== id) }));
      throw err;
    }
  },

  async deletePack(id) {
    set((s) => ({ loadingIds: [...s.loadingIds, id] }));
    try {
      await vaultApi.deleteAdminPack(id);
      set((s) => ({
        packs: s.packs.filter((candidate) => candidate.id !== id),
        loadingIds: s.loadingIds.filter((candidate) => candidate !== id),
      }));
    } catch (err) {
      set((s) => ({ loadingIds: s.loadingIds.filter((candidate) => candidate !== id) }));
      throw err;
    }
  },

  reset() {
    set({ packs: [], load: IDLE_LOAD, loadingIds: [] });
  },
}));
